import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, AuthTokens } from '@/types';
import { authService, isRequiresOTP, type RequiresOTPResponse } from '@/services/auth';

// Redirect callback for auth failures (used by api interceptors)
let authRedirectCallback: (() => void) | null = null;
export function setAuthRedirectCallback(cb: (() => void) | null) {
  authRedirectCallback = cb;
}
export function triggerAuthRedirect() {
  authRedirectCallback?.();
}

interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  _hasHydrated: boolean;

  // Actions
  /** Returns RequiresOTPResponse when email 2FA is enabled and no otp_code was provided. */
  login: (email: string, password: string, otpCode?: string) => Promise<RequiresOTPResponse | void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User) => void;
  completeOAuthSession: (tokens: { token: string; refresh_token: string; expires_at: string }) => void;
  setHasHydrated: (state: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      _hasHydrated: false,

      login: async (email: string, password: string, otpCode?: string) => {
        set({ isLoading: true, error: null });
        try {
          const loginResponse = await authService.login({
            email,
            password,
            otp_code: otpCode || undefined,
          });
          if (isRequiresOTP(loginResponse)) {
            set({ isLoading: false });
            return loginResponse;
          }
          const { user, token, refresh_token, expires_at } = authService.transformLoginResponse(loginResponse);
          set({
            user: user,
            tokens: {
              token: token,
              refresh_token: refresh_token,
              expires_at: expires_at,
            },
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Login failed',
            isLoading: false,
          });
          throw error;
        }
      },

      register: async (email: string, password: string, name: string) => {
        set({ isLoading: true, error: null });
        try {
          await authService.register({ email, password, name });
          const loginResponse = await authService.login({ email, password });
          if (isRequiresOTP(loginResponse)) {
            // New accounts never have 2FA enabled; treat as unexpected.
            set({
              error: 'Additional verification required. Please sign in again.',
              isLoading: false,
            });
            return;
          }
          const { user, token, refresh_token, expires_at } = authService.transformLoginResponse(loginResponse);
          set({
            user,
            tokens: {
              token,
              refresh_token,
              expires_at,
            },
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Registration failed',
            isLoading: false,
          });
          throw error;
        }
      },

      logout: async () => {
        try {
          await authService.logout();
        } finally {
          set({
            user: null,
            tokens: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },

      refreshToken: async () => {
        const { tokens } = get();
        if (!tokens?.refresh_token) {
          set({ isAuthenticated: false });
          return;
        }

        try {
          const response = await authService.refresh(tokens.refresh_token);
          set({
            tokens: {
              token: response.token,
              refresh_token: response.refresh_token,
              expires_at: response.expires_at,
            },
            isAuthenticated: true,
          });
        } catch {
          set({ user: null, tokens: null, isAuthenticated: false });
        }
      },

      clearError: () => set({ error: null }),

      setUser: (user: User) => set({ user }),

      completeOAuthSession: (tokens) => {
        set({
          tokens: {
            token: tokens.token,
            refresh_token: tokens.refresh_token,
            expires_at: tokens.expires_at,
          },
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      },

      setHasHydrated: (state: boolean) => set({ _hasHydrated: state }),
    }),
    {
      name: 'khiimori-auth',
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);

// Selector hooks for optimized re-renders
export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useUserRole = () => useAuthStore((state) => state.user?.role ?? 'viewer');
export const useHasHydrated = () => useAuthStore((state) => state._hasHydrated);